import { observable, action, toJS } from 'mobx'
import { unionBy } from 'lodash'
import * as Ramda from 'ramda'
import BaseStore, { IKey } from '../BaseStore'
import { checkType, taskType } from 'constants/index'
import { IAudio } from 'components/Audio'

const progressBtns: any[] = [
  {
    name: '新人进阶',
    path: 'newbie',
  },
  {
    name: '员工转正',
    path: 'regular-worker',
  },
  {
    name: '精英提升',
    path: 'elite-promotion',
  },
]

export interface IAudioConfig {
  src: string
  loop?: string
  autoplay?: string
}

enum ConfigContentMode {
  ADD = '添加',
  EDIT = '编辑',
}

export enum levelStrategies {
  CHECK_TEMPLATE = 'checkTemplate',
  CHECK_ROLES = 'checkRoles',
  DEPARTMENT_ROLES = 'departmentRoles',
  PASSABLE_STAFFS = 'passableStaffs',
  FAVORITES_TEMPLATE = 'favoritesTemplate',
  TEMPLATE_DRAFT = 'templateDraft',
  SINGLE_CHECK = 'singleCheck',
}

const getEntityListStrategies = self => {
  return {
    [levelStrategies.CHECK_TEMPLATE]: async (param, extra) => {
      try {
        const res = await self.api.level.getCheckTemplate(param, {
          showMsg: false,
        })
        self.setCheckTemplate(res)
        if (!self.currentCheck) {
          self.setCurrentCheck(res.checks.data[0], 0)
        }
      } catch (err) {
        self.setCheckTemplate({
          id: 0,
          checks: { meta: {}, data: [] },
          check_groups: [],
        })

        return Promise.reject(err)
      }
    },
    [levelStrategies.CHECK_ROLES]: async (param, extra) => {
      const res = await self.api.level.getCheckRoles(
        { page: 1, per_page: 10, ...param },
        extra,
      )
      if (extra === 'append') {
        self.setCheckRoles({
          data: self.checkRoles.concat(res.data),
          meta: res.meta,
        })
      } else {
        self.setCheckRoles(res)
      }

      return res
    },
    [levelStrategies.DEPARTMENT_ROLES]: async (param, mode) => {
      const res = await self.api.level.getDepartmentRoles({
        page: 1,
        ...param,
      })

      if (mode === 'append') {
        self.setDepartmentRoles({
          data: self.departmentRoles.concat(res.data),
          meta: res.meta,
        })
      } else {
        self.setDepartmentRoles(res)
      }
    },
    [levelStrategies.PASSABLE_STAFFS]: async (param, mode) => {
      const res = await self.api.level.getPassableStaffs({
        page: 1,
        per_page: 10,
        ...param,
      })

      if (mode === 'append') {
        self.setPassableStaffs({
          data: unionBy(self.passableStaffs, res.data, 'staff_id'),
          meta: res.meta,
        })
      } else {
        self.setPassableStaffs(res)
      }

      return Promise.resolve(res)
    },
    [levelStrategies.FAVORITES_TEMPLATE]: async (param, mode) => {
      const res = await self.api.level.getFavoritesTemplate({
        page: 1,
        per_page: 10,
        ...param,
      })

      if (mode === 'append') {
        self.setFavoritesTemplate({
          data: self.favoritesTemplate.concat(res.data),
          meta: res.meta,
        })
      } else {
        self.setFavoritesTemplate(res)
      }
    },
    [levelStrategies.SINGLE_CHECK]: async (param, mode) => {
      const res = await self.api.level.getSingleCheckDetails(param, {
        showMsg: false,
      })
      return res
    },
  }
}

const addEntityStrategies = self => {
  return {
    [levelStrategies.TEMPLATE_DRAFT]: async (param, extra?) => {
      return self.api.level.addTemplateDraft(param, extra)
    },
    [levelStrategies.FAVORITES_TEMPLATE]: async (param, extra?) => {
      return self.api.level.addFavoritesTemplate(param, extra)
    },
  }
}

const updateEntityStrategies = self => {
  return {
    [levelStrategies.FAVORITES_TEMPLATE]: async (id: IKey, param, extra?) => {
      return self.api.level.updateFavoritesTemplate(id, param, extra)
    },
  }
}

const removeEntityStrategies = self => {
  return {
    [levelStrategies.FAVORITES_TEMPLATE]: async (id: IKey, extra?) => {
      return self.api.level.removeFavoritesTemplate(id, extra)
    },
  }
}

export class LevelStore extends BaseStore {
  @observable templInfo = {} // 模板更新信息
  @observable userLessons = [] // 用户闯关记录列表
  @observable
  checkTemplate = {
    id: 0,
    checks: { meta: {}, data: [] },
    check_groups: [],
  } // 关卡模板

  orgCheckTemplate = {} // 旧关卡模板, 用户比较数据是否修改

  // 交互性音效配置
  @observable
  audioConfig = {
    src: '',
  } as IAudio

  // 背景音效配置
  @observable
  bgAudioConfig = {
    src: '',
    loop: true,
  } as IAudio

  @observable
  userCheckRecord = {
    meta: {
      check_template: {
        check_groups: [],
      },
    },
    data: [],
  } // 用户闯关记录列表

  @observable selectedGroupId = null

  @observable currentCheck = null

  @observable currentCheckIndex = 0

  @observable currentUserCheck = null

  @observable currentUserCheckIndex = 0

  @observable configContentVisible = false

  @observable groupModalVisible = false

  @observable checkSettingsVisible = false

  @observable favoritesVisible = false

  @observable currentConfigContentType = taskType.example

  @observable currentConfigContent = null

  @observable configContentList = []

  @observable configContentMeta = null

  @observable configContentMode = ConfigContentMode.ADD as ConfigContentMode // 配置页添加题目和视频弹窗的子菜单当前的编辑模式

  @observable checkRoles = [] // 关卡下的可配置角色
  @observable checkRolesMeta = null

  @observable departmentRoles = [] // 部门下的角色列表
  @observable departmentRolesMeta = null

  @observable passableStaffs = [] // 可通关的员工列表
  @observable passableStaffsMeta = null

  @observable favoritesTemplate = [] // 收藏的关卡列表
  @observable favoritesTemplateMeta = null

  constructor() {
    super()
  }

  @action
  toggleConfigContentVisible = (currentConfigContentType: string): void => {
    this.toggleConfigContentPanel(currentConfigContentType)
    this.configContentVisible = !this.configContentVisible
  }

  @action
  toggleConfigContentPanel = (currentConfigContentType: string): void => {
    this.currentConfigContentType = currentConfigContentType
  }

  @action
  toggleGroupModal = (): void => {
    this.groupModalVisible = !this.groupModalVisible
  }

  @action
  toggleCheckSettingsModal = (): void => {
    this.checkSettingsVisible = !this.checkSettingsVisible
  }

  @action
  toggleFavoritesModal = (): void => {
    this.favoritesVisible = !this.favoritesVisible
  }

  @action
  addCheckGroup = checkGroup => {
    const { check_groups } = this.checkTemplate
    this.checkTemplate = {
      ...this.checkTemplate,
      check_groups: !check_groups
        ? [].concat(checkGroup)
        : this.checkTemplate.check_groups.concat(checkGroup),
    }
  }

  @action
  updateGroup = (g: { id: number; name: string }): void => {
    this.checkTemplate = {
      ...this.checkTemplate,
      check_groups: this.checkTemplate.check_groups.map(checkGroup => {
        if (checkGroup.id === g.id) {
          checkGroup.name = g.name
        }
        return checkGroup
      }),
    }
  }

  @action
  deleteGroup = id => {
    const checks = this.checkTemplate.checks
    const checkTemplate = {
      ...this.checkTemplate,
      check_groups: this.checkTemplate.check_groups.filter(
        checkGroup => checkGroup.id !== id,
      ),
      checks: {
        ...checks,
        data: checks.data.map(check => {
          if (check.group_id === id) {
            delete check.group_id
          }
          return check
        }),
      },
    }

    if (checkTemplate.check_groups.length === 0) {
      delete checkTemplate.check_groups
    }
    this.setCheckTemplate(checkTemplate)
  }

  @action
  getConfigContentList = async (param = {}, mode = 'video'): Promise<any> => {
    let res
    if (mode === 'video') {
      res = await this.api.level.getAdvancedVideo(param)
    } else {
      res = await this.api.level.getAdvancedExample(param)
      // 统一后端不同数据结构
      res.total = res.meta.pagination.total
    }

    this.setConfigContentList(res)
  }

  @action
  toggleAudioVoice = (config: IAudio, mode = 'normal') => {
    if (mode === 'normal') {
      this.audioConfig = {
        ...this.audioConfig,
        ...config,
      }
    } else {
      this.bgAudioConfig = {
        ...this.bgAudioConfig,
        ...config,
      }
    }
  }

  @action
  publishCheckTemplate = async (params: any) => {
    try {
      const res: any = await this.api.level.publishCheckTemplate(params)
      this.setCheckTemplate({ ...this.checkTemplate, ...res })
      this.setCurrentCheck(
        res.data.checks.data[this.currentCheckIndex],
        this.currentCheckIndex,
      )
    } catch (err) {
      return Promise.reject(err)
    }
  }

  /**
   * @description 获取关卡模板更新信息
   */
  @action
  getTemplUpdateInfo = async (params: any) => {
    try {
      const res = await this.api.level.getTemplUpdateInfo(params, {
        showMsg: false,
      })
      this.templInfo = res
    } catch (err) {
      return Promise.reject(err)
    }
  }

  @action
  getUserLessons = async () => {
    const res = await this.api.level.getUserLessons()
    this.setUserLessons(res)
  }

  @action
  getUserCheckRecord = async params => {
    const res = await this.api.level.getUserCheckRecord(params)

    this.setUserCheckRecord(res)
  }

  @action
  fetchCheckTaskRecords = async id => {
    const data = await this.api.level.getCheckTaskRecords(id)
    return data
  }

  @action
  getPreviewStudyCheckRecords = async id => {
    const res = await this.api.level.previewStudyCheckRecords(id)
    return res
  }

  @action
  getPreviewUserCheckRecord = async (param, extra?) => {
    const { data } = await this.api.level.previewCheckTemplate(param, extra)
    this.setUserCheckRecord(data)
  }

  @action
  reorderCheck = (target: number, source: number): void => {
    const checks = this.checkTemplate.checks
    const copiedChecks = Array.from(checks.data)
    const ret = copiedChecks.splice(source, 1)[0]
    copiedChecks.splice(target, 0, ret)

    this.setCheckTemplate({
      ...this.checkTemplate,
      checks: {
        ...checks,
        data: copiedChecks,
      },
    })

    this.setCurrentCheck(copiedChecks[target], target)
  }

  @action
  deleteCheck = (order: number): void => {
    const checks = this.checkTemplate.checks
    this.setCheckTemplate({
      ...this.checkTemplate,
      checks: {
        ...checks,
        data: this.checkTemplate.checks.data.filter(
          (check, index) => index !== order,
        ),
      },
    })
  }

  @action
  addCheck = (check: object, order: number): void => {
    const checks = this.checkTemplate.checks
    const copiedChecks = Array.from(checks.data)
    let currentCheckIndex = 0
    if (order < 0) {
      copiedChecks.push(check)
      currentCheckIndex = copiedChecks.length - 1
      this.setCheckTemplate({
        ...this.checkTemplate,
        checks: {
          ...checks,
          data: copiedChecks,
        },
      })
    } else {
      copiedChecks.splice(order, 0, check)
      currentCheckIndex = order
      this.setCheckTemplate({
        ...this.checkTemplate,
        checks: {
          ...checks,
          data: copiedChecks,
        },
      })
    }
    this.setCurrentCheck(copiedChecks[currentCheckIndex], currentCheckIndex)
  }

  @action
  updateCheck = (check: object, order: number): void => {
    const checks = this.checkTemplate.checks
    const copiedChecks = Array.from(checks.data)

    copiedChecks.splice(order, 1, check)

    this.setCheckTemplate({
      ...this.checkTemplate,
      checks: {
        ...checks,
        data: copiedChecks,
      },
    })

    this.setCurrentCheck(copiedChecks[order], order)
  }

  /**
   * @description 添加关卡任务
   * 不同类型的关卡（check） 操作任务（增加、删除）的逻辑不同
   * 综合关
   *  - 每一个视频作为关卡下单独任务（task）
   *  - 如果遇到连续的试题 entity 操作将操作单一的任务进行，例如连续添加（删除）3 道题，这 3 道题将作为一个整体添加（删除）到一个任务中
   * 习题关/必通关
   *  - 只会添加一个任务，所有的试题将存放在 example_titles/exam_titles 中，所有操作均针对以上两者
   */
  @action
  addTask = async (order, task) => {
    const checks = this.checkTemplate.checks
    const copiedChecks: any = Array.from(checks.data)

    for (let i = 0, len = copiedChecks.length; i < len; i++) {
      if (order === i) {
        const tasks = copiedChecks[order].tasks
        const targetIndex = tasks.findIndex(item => item.id === task.id)
        if (targetIndex < 0) {
          copiedChecks[order].tasks.push(task)
        } else {
          copiedChecks[order].tasks.splice(targetIndex, 1, task)
        }
        break
      }
    }

    this.setCheckTemplate({
      ...this.checkTemplate,
      checks: {
        ...checks,
        data: copiedChecks,
      },
    })
    this.setCurrentCheck(copiedChecks[order], order)
  }

  @action
  reorderTask = (order: number, id: number, mode: number) => {
    const checks = this.checkTemplate.checks
    const copiedChecks: any = Array.from(checks.data)
    const type = copiedChecks[order].type
    const tasks = copiedChecks[order].tasks
    let currentIndex
    let dataList

    if (type === checkType.integration) {
      dataList = tasks
      currentIndex = dataList.findIndex(task => task.id === id)
    } else if (type === checkType.example) {
      dataList = tasks[0].example_titles
      currentIndex = dataList.findIndex(title => title.id === id)
    } else {
      dataList = tasks[0].exam_titles
      currentIndex = dataList.findIndex(title => title.id === id)
    }

    // 如果当前排序元素是第一个并且向上排序或是最后一个向下排序，则直接返回
    if (
      (currentIndex === 0 && mode < 0) ||
      (currentIndex === dataList.length - 1 && mode > 0)
    ) {
      return
    }

    const ret = dataList.splice(currentIndex + mode, 1)
    dataList.splice(currentIndex, 0, ret[0])

    this.setCheckTemplate({
      ...this.checkTemplate,
      checks: {
        ...checks,
        data: copiedChecks,
      },
    })

    this.setCurrentCheck(copiedChecks[order], order)
  }
  /**
   * @description 删除关卡任务
   */
  @action
  deleteTask = (order, id) => {
    const checks = this.checkTemplate.checks
    const copiedChecks: any = Array.from(checks.data)
    if (
      copiedChecks[order].tasks.length === 1 &&
      copiedChecks[order].type !== checkType.integration
    ) {
      const task = copiedChecks[order].tasks[0]
      let titles,
        titleKey = ''
      if (taskType.example === task.type) {
        titleKey = 'example_titles'
        titles = task.example_titles
      } else {
        titleKey = 'exam_titles'
        titles = task.exam_titles
      }

      copiedChecks[order].tasks[0][titleKey] = titles.filter(
        title => title.id !== id,
      )
    } else {
      for (let i = 0, len = copiedChecks.length; i < len; i++) {
        if (order === i) {
          const tasks = copiedChecks[order].tasks
          copiedChecks[order].tasks = tasks.filter(task => task.id !== id)
          break
        }
      }
    }
    this.setCheckTemplate({
      ...this.checkTemplate,
      checks: {
        ...checks,
        data: copiedChecks,
      },
    })

    this.setCurrentCheck(copiedChecks[order], order)
  }

  @action
  getCheckTaskRecord = async (id, order) => {
    const res = await this.api.level.getCheckTaskRecord(id)
    return res.status
    // return this.setCheckTaskRecord(res, order)
  }

  @action
  setConfigContentList = res => {
    this.configContentList = res.data
    this.configContentMeta = { total: res.total }
  }

  @action
  setUserLessons = lessons => {
    for (let i = 0; i < lessons.length; i++) {
      lessons[i].path = progressBtns[i].path
    }
    this.userLessons = lessons
  }

  @action
  setUserCheckRecord = record => {
    this.userCheckRecord = record
  }

  @action
  setCurrentCheck = (check, index?) => {
    this.currentCheck = check
    this.currentCheckIndex = index
  }

  setTasks = (task, index: number = 0) => {
    // 为每个关卡设置task
    const { checkTemplate, setCheckTemplate, setOrgCheckTemplate } = this
    // 获取关卡列表
    const newCheckTemplate = toJS(checkTemplate)
    const newCheck = Ramda.pathOr(false, ['checks', 'data'], newCheckTemplate)
    if (newCheck) {
      newCheck.splice(index, 1, task)
      newCheckTemplate.checks.data = newCheck
      setCheckTemplate(newCheckTemplate)
      setOrgCheckTemplate(newCheckTemplate) // 设置旧关卡模板
      return true
    } else {
      return false
    }
  }

  @action
  setCurrentUserCheck = (check, index?) => {
    this.currentUserCheck = check
    this.currentUserCheckIndex = index
  }
  @action
  setCheckTaskRecord = (record, order) => {
    const { meta, data } = this.userCheckRecord
    const { check_task_records } = data[order]

    for (let i = 0, len = check_task_records.length; i < len; i++) {
      if (check_task_records[i].id === record.id) {
        check_task_records[i] = {
          ...check_task_records[i],
          status: record.status,
        }
        break
      }
    }

    data.splice(order, 1, {
      ...data[order],
      check_task_records,
    })

    this.userCheckRecord = {
      meta,
      data,
    }

    return record.status
  }

  @action
  setSelectedGroup = groupId => {
    this.selectedGroupId = groupId
  }

  @action
  setCurrentConfigContent = (currentConfigContent: any): void => {
    this.currentConfigContent = currentConfigContent
    this.configContentMode = currentConfigContent
      ? ConfigContentMode.EDIT
      : ConfigContentMode.ADD
  }

  @action
  setCheckTemplate = (template: any) => {
    this.checkTemplate = template
  }

  setOrgCheckTemplate = (template: any) => {
    this.orgCheckTemplate = template
  }

  @action
  setCheckRoles = checkRoles => {
    this.checkRoles = checkRoles.data
    this.checkRolesMeta = checkRoles.meta
  }

  @action
  setDepartmentRoles = departmentRoles => {
    this.departmentRoles = departmentRoles.data
    this.departmentRolesMeta = departmentRoles.meta
  }

  @action
  setPassableStaffs = passableStaffs => {
    this.passableStaffs = passableStaffs.data
    this.passableStaffsMeta = passableStaffs.meta
  }

  @action
  setFavoritesTemplate = favoritesTemplate => {
    this.favoritesTemplate = favoritesTemplate.data
    this.favoritesTemplateMeta = favoritesTemplate.meta
  }

  @action
  protected async getList(namespace, param, extra?): Promise<any> {
    return (
      getEntityListStrategies(this)[namespace] &&
      getEntityListStrategies(this)[namespace](param, extra)
    )
  }
  protected async add(namespace: string, entity: any, extra?) {
    return (
      addEntityStrategies(this)[namespace] &&
      addEntityStrategies(this)[namespace](entity, extra)
    )
  }

  protected async update(
    namespace: string,
    id: IKey,
    entity: any,
    extra?,
  ): Promise<any> {
    return (
      updateEntityStrategies(this)[namespace] &&
      updateEntityStrategies(this)[namespace](id, entity, extra)
    )
  }

  protected async remove(namespace: string, id: IKey, extra?): Promise<any> {
    return (
      removeEntityStrategies(this)[namespace] &&
      removeEntityStrategies(this)[namespace](id, extra)
    )
  }
}

export default new LevelStore()
